import { matchPath } from 'react-router-dom';
import type { HelpContext } from './types';

export const GLOBAL_CONTEXT_KEY = 'global';

export interface ResolvedContext {
  context: HelpContext;
  params: Record<string, string>;
}

export function findContext(key: string, contexts: HelpContext[]): HelpContext | undefined {
  return contexts.find((ctx) => ctx.key === key);
}

export function globalContext(contexts: HelpContext[]): HelpContext {
  const global = findContext(GLOBAL_CONTEXT_KEY, contexts);
  if (!global) throw new Error('help contexts must include a global root');
  return global;
}

/** Map the current location to the page context whose route pattern matches it. */
export function resolveContext(pathname: string, contexts: HelpContext[]): ResolvedContext {
  for (const context of contexts) {
    for (const pattern of context.routes ?? []) {
      const match = matchPath({ path: pattern, end: true }, pathname);
      if (match) {
        const params: Record<string, string> = {};
        for (const [name, value] of Object.entries(match.params)) {
          if (value !== undefined) params[name] = value;
        }
        return { context, params };
      }
    }
  }
  return { context: globalContext(contexts), params: {} };
}

/** The context, then each ancestor, ending at global. Unknown keys resolve to [global]. */
export function contextChain(key: string, contexts: HelpContext[]): HelpContext[] {
  const chain: HelpContext[] = [];
  let current = findContext(key, contexts);
  const seen = new Set<string>();
  while (current && !seen.has(current.key)) {
    seen.add(current.key);
    chain.push(current);
    current = current.parent ? findContext(current.parent, contexts) : undefined;
  }
  if (chain.length === 0 || chain[chain.length - 1].key !== GLOBAL_CONTEXT_KEY) {
    chain.push(globalContext(contexts));
  }
  return chain;
}

/** The parent shown in the breadcrumb: null at page level (parent is global) and at the root. */
export function breadcrumbParent(context: HelpContext, contexts: HelpContext[]): HelpContext | null {
  if (!context.parent || context.parent === GLOBAL_CONTEXT_KEY) return null;
  return findContext(context.parent, contexts) ?? null;
}

/** The context a HelpPopover's `helpKey` bridges into, via `aliasOf`. */
export function contextKeyForHelpKey(helpKey: string, contexts: HelpContext[]): string | null {
  return contexts.find((ctx) => ctx.aliasOf === helpKey)?.key ?? null;
}

/** Fill `:param` placeholders from the current route; null when one cannot be filled. */
export function resolveCtaTarget(target: string, params: Record<string, string>): string | null {
  let unresolved = false;
  const resolved = target.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) {
      unresolved = true;
      return '';
    }
    return encodeURIComponent(value);
  });
  return unresolved ? null : resolved;
}
